import type React from "react"
import { useCallback, useRef } from "react"

// Why these popups portal out and close on scroll instead of being contained:
// docs/kb/decisions/2026-08-12-finance-row-popups-portal-to-body.md

/**
 * Escape as rc-select and rc-picker actually read it.
 *
 * Both test the legacy `keyCode`, and Chrome ignores `keyCode` passed to the
 * constructor — it stays `0` — so it has to be forced onto the instance or the
 * Select never closes outside jsdom.
 */
function escapeKeyDown(): KeyboardEvent {
  const event = new KeyboardEvent("keydown", {
    key: "Escape",
    bubbles: true,
    cancelable: true
  })

  Object.defineProperty(event, "keyCode", { get: () => 27 })
  Object.defineProperty(event, "which", { get: () => 27 })

  return event
}

/**
 * Closes an open row popup, leaving focus on the control that owns it.
 *
 * `scope` bounds the search to the table that scrolled, so a popup open
 * elsewhere on the page is left alone. Escape rather than blur: a keyboard user
 * tabbing into an off-screen column scrolls the table themselves, and blurring
 * would strand them on `document.body` with no focus ring.
 */
export function closeOpenRowPopup(scope: Document | Element = document): void {
  const doc = scope.ownerDocument ?? (scope as Document)
  const active = doc.activeElement as HTMLElement | null

  if (!active || !scope.contains(active)) return
  if (!active.closest(".ant-select, .ant-picker")) return

  active.dispatchEvent(escapeKeyDown())
}

/**
 * Composes a table's `onScroll` so a horizontal scroll also closes any open row
 * popup.
 */
export function useCloseRowPopupsOnHScroll(
  onScroll?: React.UIEventHandler<HTMLDivElement>
): React.UIEventHandler<HTMLDivElement> {
  // Seeded at the resting position: a lazy baseline spends the first event
  // learning it, so a gesture emitting only one — a wheel notch, a programmatic
  // scrollTo — would never close anything.
  const lastScrollLeft = useRef(0)

  return useCallback(
    (event) => {
      const container = event.currentTarget

      // Vertical is left alone — antd keeps the popup aligned with its trigger
      // there, and this handler also drives the infinite-scroll paging, so every
      // event still forwards.
      if (container.scrollLeft !== lastScrollLeft.current) {
        closeOpenRowPopup(container)
      }
      lastScrollLeft.current = container.scrollLeft
      onScroll?.(event)
    },
    [onScroll]
  )
}
